import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
  type ReactNode,
} from "react"
import { type AggregationMethod } from "~/components/reusable/AggregationSelector"
import { type CustomerTableColumnId } from "~/components/reusable/CustomerColumnChooser"
import {
  type ColumnChooserColumnMetadata,
  type ColumnChooserCopy,
} from "~/components/planning/columnChooser/ColumnChooserDialog"
import { type OptionType } from "~/models/enum"
import { type ColumnGroups, commitShownColumns } from "~/utils/gridOwnedColumns"

export type TableCapabilities = {
  aggregationOptions?: AggregationMethod[]
  // Live aggregation stack + setter, so the display-options popover reads and writes the
  // table's actual grouping (same pattern as buyGroupByProps value/onChange).
  aggregationValue?: string[]
  setAggregations?: (value: string[]) => void
  aggregationMeta?: {
    extraDataFields: string[]
    collectionExtraDataFields: string[]
    hasCollectionContext: boolean
  }
  /**
   * `owner: "grid"`: the table's grid owns column visibility (ProductTableV2), so the chooser reads
   * it from the grid. Otherwise the table passes the shown ids.
   */
  columnChooserProps?: ColumnChooserCapability &
    ({ owner: "grid" } | { owner?: "props"; displayedColumnIds: string[] })
  /** Checks an applied view's columns against the table's. */
  onViewApplied?: (viewColumnIds: readonly string[]) => void
  customerColumnChooserProps?: {
    availableColumnIds: CustomerTableColumnId[]
    displayedColumnIds: string[]
    setDisplayedColumnIds: (ids: string[]) => void
  }
  gridOwnedChooserProps?: GridOwnedChooserCapability
  columnChooserDialogProps?: {
    columnIds: string[]
    displayedColumnIds: string[]
    setDisplayedColumnIds: (ids: string[]) => void
    columnMetadata: ColumnChooserColumnMetadata[]
    nonRemovableColumns: string[]
    defaultColumnIds: string[]
    storageKey: string
  }
  buyGroupByProps?: {
    options: { value: string; label: string }[]
    value: string
    onChange: (value: string) => void
  }
  groupByLevelsProps?: {
    options: { value: string; label: string }[]
    value: string[]
    onChange: (value: string[]) => void
    /**
     * Always the innermost level, added implicitly. A primary in `leafLike` gets no secondary; any
     * other defaults to `leafLike[0]`. Unset means free-form levels up to `maxLevels`.
     */
    implicitLeaf?: string
    leafLike?: string[]
    /** Free-form mode only; defaults to 3. */
    maxLevels?: number
  }
  search?: {
    options?: OptionType[]
    placeholder?: string
    // Lets a master-detail table collapse first; otherwise its open detail row is orphaned.
    onBeforeChange?: () => void
  }
}

type ColumnChooserCapability = {
  columnIds: string[]
  setDisplayedColumnIds: (ids: string[]) => void
  defaultColumnIds: string[]
  fallbackColumnTitles?: Record<string, string>
}

/** The chooser of a `useGridOwnedColumns` table, which commits through `commitShownColumns`. */
export type GridOwnedChooserCapability = {
  columnIds: string[]
  defaultColumnIds: readonly string[]
  fallbackColumnTitles?: Record<string, string>
  /** Chooser entries that stand for several grid columns (see {@link ColumnGroups}). */
  columnGroups?: ColumnGroups
  /** Registered by identity: memoize it, or keep it a module constant. */
  chooser: ColumnChooserCopy
}

/**
 * Where the table's grid owns column visibility, its default columns and how to show a set of
 * them; `undefined` otherwise.
 */
export function gridOwnedChooserOf(
  capabilities: TableCapabilities | null | undefined,
  tableId: string,
): { defaultColumnIds: readonly string[]; showColumns: (ids: string[]) => void } | undefined {
  const productChooser = capabilities?.columnChooserProps
  if (productChooser?.owner === "grid") {
    const { defaultColumnIds, setDisplayedColumnIds } = productChooser
    return { defaultColumnIds, showColumns: setDisplayedColumnIds }
  }
  const chooser = capabilities?.gridOwnedChooserProps
  if (!chooser) return undefined
  return {
    defaultColumnIds: chooser.defaultColumnIds,
    showColumns: (ids) => commitShownColumns(tableId, chooser.columnIds, ids),
  }
}

const chooserKeyOf = (props: NonNullable<TableCapabilities["columnChooserProps"]>) =>
  [
    props.columnIds.join("|"),
    props.owner === "grid" ? "grid" : props.displayedColumnIds.join("|"),
    props.defaultColumnIds.join("|"),
    titlesKey(props.fallbackColumnTitles),
  ].join("::")

const gridOwnedChooserKeyOf = (props: GridOwnedChooserCapability) =>
  [
    props.columnIds.join("|"),
    props.defaultColumnIds.join("|"),
    titlesKey(props.fallbackColumnTitles),
    Object.entries(props.columnGroups ?? {})
      .map(([id, members]) => `${id}=${members.join(",")}`)
      .join("|"),
  ].join("::")

type RegistryEntry = {
  capabilities: TableCapabilities
  consumerCount: number
}

type Listener = () => void

type TableRegistryStore = {
  register: (tableId: string, capabilities: TableCapabilities) => void
  unregister: (tableId: string) => void
  addConsumer: (tableId: string) => void
  removeConsumer: (tableId: string) => void
  subscribe: (listener: Listener) => () => void
  getCapabilities: (tableId: string) => TableCapabilities | null
  hasConsumer: (tableId: string) => boolean
}

const TableRegistryContext = createContext<TableRegistryStore | null>(null)

export const TableRegistryProvider = ({ children }: { children: ReactNode }) => {
  const entriesRef = useRef<Map<string, RegistryEntry>>(new Map())
  const listenersRef = useRef<Set<Listener>>(new Set())

  const store = useMemo<TableRegistryStore>(() => {
    const notify = () => listenersRef.current.forEach((l) => l())

    const getEntry = (tableId: string): RegistryEntry =>
      entriesRef.current.get(tableId) ?? { capabilities: {}, consumerCount: 0 }

    return {
      register: (tableId, capabilities) => {
        const existing = getEntry(tableId)
        entriesRef.current.set(tableId, { ...existing, capabilities })
        notify()
      },
      unregister: (tableId) => {
        const existing = entriesRef.current.get(tableId)
        if (!existing) return
        if (existing.consumerCount > 0) {
          entriesRef.current.set(tableId, { ...existing, capabilities: {} })
        } else {
          entriesRef.current.delete(tableId)
        }
        notify()
      },
      addConsumer: (tableId) => {
        const existing = getEntry(tableId)
        entriesRef.current.set(tableId, {
          ...existing,
          consumerCount: existing.consumerCount + 1,
        })
        notify()
      },
      removeConsumer: (tableId) => {
        const existing = entriesRef.current.get(tableId)
        if (!existing) return
        const nextCount = Math.max(0, existing.consumerCount - 1)
        const hasCaps =
          !!existing.capabilities.aggregationOptions ||
          !!existing.capabilities.columnChooserProps ||
          !!existing.capabilities.customerColumnChooserProps ||
          !!existing.capabilities.gridOwnedChooserProps ||
          !!existing.capabilities.buyGroupByProps ||
          !!existing.capabilities.groupByLevelsProps ||
          !!existing.capabilities.search
        if (nextCount === 0 && !hasCaps) {
          entriesRef.current.delete(tableId)
        } else {
          entriesRef.current.set(tableId, { ...existing, consumerCount: nextCount })
        }
        notify()
      },
      subscribe: (listener) => {
        listenersRef.current.add(listener)
        return () => {
          listenersRef.current.delete(listener)
        }
      },
      getCapabilities: (tableId) => {
        const entry = entriesRef.current.get(tableId)
        return entry?.capabilities ?? null
      },
      hasConsumer: (tableId) => {
        const entry = entriesRef.current.get(tableId)
        return (entry?.consumerCount ?? 0) > 0
      },
    }
  }, [])

  return <TableRegistryContext.Provider value={store}>{children}</TableRegistryContext.Provider>
}

const useStore = () => useContext(TableRegistryContext)

const titlesKey = (titles: Record<string, string> | undefined) =>
  Object.entries(titles ?? {})
    .map(([id, title]) => `${id}=${title}`)
    .join("|")

export const useRegisterTable = (tableId: string | undefined, capabilities: TableCapabilities) => {
  const store = useStore()
  const capsRef = useRef(capabilities)
  useEffect(() => {
    capsRef.current = capabilities
  })

  const aggKey = capabilities.aggregationOptions?.join("|") ?? ""
  const aggValueKey = capabilities.aggregationValue?.join("|") ?? ""
  const metaKey = capabilities.aggregationMeta
    ? `${capabilities.aggregationMeta.extraDataFields.join("|")}::${capabilities.aggregationMeta.collectionExtraDataFields.join("|")}::${capabilities.aggregationMeta.hasCollectionContext}`
    : ""
  const chooserKey = capabilities.columnChooserProps
    ? chooserKeyOf(capabilities.columnChooserProps)
    : ""
  const customerChooserKey = capabilities.customerColumnChooserProps
    ? `${capabilities.customerColumnChooserProps.availableColumnIds.join("|")}::${capabilities.customerColumnChooserProps.displayedColumnIds.join("|")}`
    : ""
  const gridOwnedChooserKey = capabilities.gridOwnedChooserProps
    ? gridOwnedChooserKeyOf(capabilities.gridOwnedChooserProps)
    : ""
  const dialogChooserKey = capabilities.columnChooserDialogProps
    ? `${capabilities.columnChooserDialogProps.columnIds.join("|")}::${capabilities.columnChooserDialogProps.displayedColumnIds.join("|")}::${capabilities.columnChooserDialogProps.nonRemovableColumns.join("|")}::${capabilities.columnChooserDialogProps.defaultColumnIds.join("|")}::${capabilities.columnChooserDialogProps.columnMetadata.map((c) => `${c.id}=${c.displayName ?? ""}`).join("|")}`
    : ""
  const buyGroupByKey = capabilities.buyGroupByProps
    ? `${capabilities.buyGroupByProps.options.map((o) => o.value).join("|")}::${capabilities.buyGroupByProps.value}`
    : ""
  const groupByLevelsKey = capabilities.groupByLevelsProps
    ? `${capabilities.groupByLevelsProps.options.map((o) => o.value).join("|")}::${capabilities.groupByLevelsProps.value.join("|")}::${capabilities.groupByLevelsProps.maxLevels ?? ""}::${capabilities.groupByLevelsProps.implicitLeaf ?? ""}::${(capabilities.groupByLevelsProps.leafLike ?? []).join("|")}`
    : ""
  const searchKey = capabilities.search
    ? `${(capabilities.search.options ?? []).map((o) => o.value).join("|")}::${capabilities.search.placeholder ?? ""}`
    : ""

  const hasViewCheck = !!capabilities.onViewApplied

  useEffect(() => {
    if (!store || !tableId) return
    store.register(tableId, capsRef.current)
    return () => store.unregister(tableId)
  }, [
    store,
    tableId,
    aggKey,
    aggValueKey,
    metaKey,
    chooserKey,
    customerChooserKey,
    gridOwnedChooserKey,
    capabilities.gridOwnedChooserProps?.chooser,
    dialogChooserKey,
    buyGroupByKey,
    groupByLevelsKey,
    searchKey,
    hasViewCheck,
  ])
}

/** Reads a table's capabilities when called, not when rendered: for callbacks that outlive a render. */
export const useReadTableCapabilities = () => {
  const store = useStore()
  return useCallback(
    (tableId: string): TableCapabilities | null => store?.getCapabilities(tableId) ?? null,
    [store],
  )
}

export const useTableRegistry = (tableId: string | undefined): TableCapabilities | null => {
  const store = useStore()
  const subscribe = useCallback(
    (listener: () => void) => (store ? store.subscribe(listener) : () => {}),
    [store],
  )
  const getSnapshot = useCallback(
    () => (store && tableId ? store.getCapabilities(tableId) : null),
    [store, tableId],
  )
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

export const useConsumeTable = (tableId: string | undefined) => {
  const store = useStore()
  useEffect(() => {
    if (!store || !tableId) return
    store.addConsumer(tableId)
    return () => store.removeConsumer(tableId)
  }, [store, tableId])
}

export const useTableHasConsumer = (tableId: string | undefined): boolean => {
  const store = useStore()
  const subscribe = useCallback(
    (listener: () => void) => (store ? store.subscribe(listener) : () => {}),
    [store],
  )
  const getSnapshot = useCallback(
    () => (store && tableId ? store.hasConsumer(tableId) : false),
    [store, tableId],
  )
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}
